import React, { useId, useState } from "react";
import { Check, CheckCircle } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useSettings } from "@/context/SettingsContext";
import { orderedCodes } from "@/lib/currencies";
import { cn } from "@/lib/utils";
import { LANGUAGES, languageName, localeOf, pick } from "@/lib/languages";
import { chromeL } from "@/i18n/chrome";
import { appShellL } from "@/i18n/appShell";
import { isNativeApp } from "@/lib/nativeApp";
import AppSheet from "@/components/app/AppSheet";

/**
 * A currency by its own name in the visitor's language (Intl): "franc CFA
 * (BCEAO)" for XOF and "franc CFA (BEAC)" for XAF, so the two FCFA are told
 * apart; the table's English name when Intl has none.
 */
export function currencyName(code, lang, currencies) {
  try {
    const name = new Intl.DisplayNames([localeOf(lang)], { type: "currency" }).of(code);
    if (name && name !== code) return name.charAt(0).toLocaleUpperCase(localeOf(lang)) + name.slice(1);
  } catch { /* older engines: the table's name */ }
  return currencies?.[code]?.name || code;
}

const TRIGGERS = {
  // The header: a quiet pill on white.
  header: {
    trigger: "locale-trigger", language: "locale-language", currency: "locale-currency",
    className: "h-11 rounded-full border border-touki bg-white px-3.5 text-label-lg font-semibold text-touki-black hover:bg-touki-light",
  },
  // Phones and tablets: a small chip with the two codes, "FR · XOF" (owner,
  // 2026-10-24); it opens the same panel. The visible codes stay in its name.
  compact: {
    trigger: "locale-trigger-compact", language: "locale-language-compact", currency: "locale-currency-compact", short: true,
    className: "h-11 rounded-full border border-touki bg-white px-2.5 text-label-md font-semibold text-touki-black hover:bg-touki-light",
  },
  // The footer: the same control on ink (its focus ring turns white there, .tk-on-ink).
  footer: {
    trigger: "footer-locale-trigger", language: "footer-language", currency: "footer-currency",
    className: "min-h-11 rounded-full border border-white/30 px-3.5 text-label-lg font-semibold text-white hover:border-white hover:bg-white/10",
  },
};

/**
 * The languages on top and the currencies below, the ones in use ticked; a
 * choice applies at once. The header's panel and the phone menu's
 * "Language and currency" line open these same lists. `testPrefix` keeps
 * the phone menu's test ids apart from the panel's.
 */
export function LocaleChoices({ testPrefix = "" }) {
  const { lang, siteLang = lang, setLang, currency, setCurrency, currencies } = useSettings();
  const w = pick(chromeL, lang).locale;
  const codes = orderedCodes(currencies);
  const id = useId();
  return (
    <div className="space-y-5">
      <section aria-labelledby={`${id}-language`}>
        <h3 id={`${id}-language`} className="mb-2 text-label-lg font-bold text-touki-black">{w.language}</h3>
        <div className="grid grid-cols-2 gap-1" role="group" aria-labelledby={`${id}-language`} data-testid={`${testPrefix}locale-languages`}>
          {LANGUAGES.map((option) => {
            const chosen = option.code === siteLang;
            return (
              <button key={option.code} type="button" aria-pressed={chosen} lang={option.code}
                onClick={() => setLang(option.code)} data-testid={`${testPrefix}lang-${option.code}`}
                className={cn("flex min-h-11 items-center justify-between gap-2 rounded-md px-3 text-start text-body-sm text-touki-black transition-colors duration-fast hover:bg-touki-light",
                  chosen && "bg-touki-light font-bold")}>
                {/* The name keeps its own direction; the list keeps the page's alignment. */}
                <bdi dir={option.dir}>{option.name}</bdi>
                {chosen ? <Check size={16} weight="bold" aria-hidden="true" className="shrink-0" /> : null}
              </button>
            );
          })}
        </div>
      </section>
      <section aria-labelledby={`${id}-currency`}>
        <h3 id={`${id}-currency`} className="mb-2 text-label-lg font-bold text-touki-black">{w.currency}</h3>
        <div className="grid grid-cols-2 gap-1" role="group" aria-labelledby={`${id}-currency`} data-testid={`${testPrefix}locale-currencies`}>
          {codes.map((code) => {
            const chosen = code === currency;
            return (
              <button key={code} type="button" aria-pressed={chosen} onClick={() => setCurrency(code)}
                data-testid={`${testPrefix}cur-${code.toLowerCase()}`}
                className={cn("flex min-h-11 items-center justify-between gap-2 rounded-md px-3 py-1.5 text-start text-touki-black transition-colors duration-fast hover:bg-touki-light",
                  chosen && "bg-touki-light")}>
                <span className="flex min-w-0 flex-col">
                  <span className={cn("text-label-lg tabular-nums", chosen ? "font-bold" : "font-semibold")}>
                    {code} <span className="font-normal text-touki-muted">{currencies[code]?.symbol !== code ? currencies[code]?.symbol : ""}</span>
                  </span>
                  <span className="truncate text-caption text-touki-muted">{currencyName(code, lang, currencies)}</span>
                </span>
                {chosen ? <Check size={16} weight="bold" aria-hidden="true" className="shrink-0" /> : null}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

/**
 * The same choices as the apps draw them (the approved design, 05-home-locale):
 * the languages as chips, the chosen one in night blue, then the currencies as
 * rows of their name and "XOF · FCFA", the chosen one ticked. A language
 * applies at once and the sheet stays for the currency; a currency closes it.
 */
export function AppLocaleChoices({ onDone }) {
  const { lang, siteLang = lang, setLang, currency, setCurrency, currencies } = useSettings();
  const w = pick(chromeL, lang).locale;
  const id = useId();
  return (
    <>
      <span id={`${id}-language`} className="tk-app-lab">{w.language}</span>
      <div className="tk-app-chips" role="group" aria-labelledby={`${id}-language`} data-testid="locale-languages">
        {LANGUAGES.map((option) => (
          <button key={option.code} type="button" className="tk-app-chip tk-press" aria-pressed={option.code === siteLang} lang={option.code}
            onClick={() => setLang(option.code)} data-testid={`lang-${option.code}`}>
            <bdi dir={option.dir}>{option.name}</bdi>
          </button>
        ))}
      </div>
      <span id={`${id}-currency`} className="tk-app-lab">{w.currency}</span>
      <div className="tk-app-list" role="group" aria-labelledby={`${id}-currency`} data-testid="locale-currencies">
        {orderedCodes(currencies).map((code) => {
          const symbol = currencies[code]?.symbol;
          return (
            <button key={code} type="button" className="tk-app-li" aria-pressed={code === currency}
              onClick={() => { setCurrency(code); onDone(); }} data-testid={`cur-${code.toLowerCase()}`}>
              <span className="min-w-0 flex-1">
                <span className="tk-app-li-t truncate">{currencyName(code, lang, currencies)}</span>
                <span className="tk-app-li-s tabular-nums" translate="no">{symbol && symbol !== code ? `${code} · ${symbol}` : code}</span>
              </span>
              {code === currency ? <CheckCircle size={20} aria-hidden="true" className="shrink-0 text-touki-dark" /> : null}
            </button>
          );
        })}
      </div>
    </>
  );
}

/**
 * One control for both, the way Booking.com does it: the language, by its
 * own name, and the currency code open a panel with the languages on top and
 * the currencies below. A choice applies at once and the panel stays open for
 * the other. No flags: a flag is a country, not a language.
 */
export default function LocalePicker({ className = "", variant = "header" }) {
  const { lang, siteLang = lang, currency } = useSettings();
  const [open, setOpen] = useState(false);
  const w = pick(chromeL, lang).locale;
  const ids = TRIGGERS[variant] || TRIGGERS.header;
  const trigger = (
    <button type="button" data-testid={ids.trigger} title={w.open}
      className={cn("inline-flex items-center gap-1.5 whitespace-nowrap transition-colors duration-fast", ids.className, className)}>
      <span className="sr-only">{w.open}: </span>
      <span data-testid={ids.language} lang={siteLang}>{ids.short ? siteLang.toUpperCase() : languageName(siteLang)}</span>
      <span aria-hidden="true" className="opacity-60">·</span>
      <span data-testid={ids.currency} translate="no" className="tabular-nums">{currency}</span>
    </button>
  );
  // The apps open the same lists in a sheet from the bottom of the screen
  // (owner, 2026-10-24: the approved Android design).
  if (isNativeApp()) {
    return (
      <>
        {React.cloneElement(trigger, { onClick: () => setOpen(true), "aria-haspopup": "dialog" })}
        <AppSheet open={open} onOpenChange={setOpen} title={pick(appShellL, lang).sheets.locale} data-testid="locale-panel" bodyClassName="pb-0">
          <AppLocaleChoices onDone={() => setOpen(false)} />
        </AppSheet>
      </>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger}
      </PopoverTrigger>
      {/* On a phone the panel keeps the page's 16 px gutters (owner, 2026-10-24: opened from the small chip). */}
      <PopoverContent align="end" collisionPadding={16} className="w-[min(calc(100vw_-_2rem),460px)] p-0" data-testid="locale-panel">
        <div className="max-h-[70vh] overflow-y-auto p-4">
          <LocaleChoices />
        </div>
        <div className="flex justify-end border-t border-touki p-2">
          <button type="button" onClick={() => setOpen(false)} data-testid="locale-done"
            className="inline-flex min-h-11 items-center rounded-md bg-touki-dark px-5 text-button-md font-semibold text-white transition-colors duration-fast hover:bg-touki-dark-hover">
            {w.done}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
